import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';
import { IVipLevel, IVipLevelsResponse } from 'models/vip/i-vip-level.interface';

import { getVipLevels } from 'services/vip-levels.service';

import { queryKeys } from './query-keys';

export interface UseVipLevelsReturn {
  levels: IVipLevel[];
  currentLevel: number | null;
  currentPoints: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchVipLevels = async (): Promise<IVipLevelsResponse> => {
  const response = await getVipLevels(new AbortController().signal);
  return response.data;
};

export const useVipLevelsQuery = (): UseVipLevelsReturn => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Public ladder: loads even without a wallet (current_level comes back null). The wallet is
    // in the key so connecting/disconnecting fetches the right per-user current_level.
    queryKey: queryKeys.vipLevels.list(walletAddress),
    queryFn: fetchVipLevels,
    staleTime: 60000,
    retry: 1,
  });

  return {
    levels: data?.levels ?? [],
    currentLevel: data?.current_level ?? null,
    currentPoints: data?.current_points ?? 0,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch VIP levels') : null,
    refetch,
  };
};
